import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Typography,
  Spin,
  Empty,
  App,
  Segmented,
  Dropdown,
  MenuProps,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  EyeOutlined,
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  PrinterOutlined,
  MoreOutlined,
  CheckOutlined,
  ScissorOutlined,
  StopOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useGetOrdersPagedQuery, useProcessPaymentMutation, useCloseInternalConsumptionOrderMutation, useCancelOrderMutation, useApplyDiscountMutation } from '../../services/ordersApi';
import { useGetPaymentMethodsQuery } from '../../services/paymentMethodsApi';
import { useGetFacturacionConfigQuery } from '../../services/facturacionApi';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { SolicitarFacturaModal } from '../facturacion';
import FacturaDetalleModal from '../facturacion/FacturaDetalleModal';
import { estadoVisibleFactura, ETIQUETA_ESTADO_FACTURA } from '../../utils/facturaEstado';
import { Order, OrderStatus, OrderFilter } from '../../types/orders';
import OrderDetailsModal from './modals/OrderDetailsModal';
import CancelOrderModal from './modals/CancelOrderModal';
import { getErrorMessage, getRetryableWriteErrorMessage } from '../../utils/errorHelpers';
import { discountLabel } from '../../utils/discounts';
import { isOrderAlreadySplitError, ORDER_ALREADY_SPLIT_NOTICE } from '../../utils/paymentErrors';
import PaymentModal, { PaymentEntry } from '../tables/modals/PaymentModal';
import DiscountModal, { DiscountData } from '../tables/modals/DiscountModal';
import PreCheckModal from '../tables/modals/PreCheckModal';
import SplitBillModal from '../splits/SplitBillModal';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useHubEvent } from '../../hooks/useHubEvent';
import { ORDERS_CHANGED_EVENT, RECONNECTED_EVENT } from '../../services/realtimeHub';
import { printAgentService } from '../../services/printAgentService';
import { buildOrderTicketPreview, withPrintAnimation } from '../../services/printAnimation';
import { bluetoothPrintService } from '../../services/bluetoothPrintService';
import { useCloudPrint } from '../../hooks/useCloudPrint';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';
import { isAdmin, isManager, canProcessPayments, canCancelOrders, canApplyDiscounts } from '../../utils/roleHelpers';
import { tablesApi } from '../../services/tablesApi';
import { useAppDispatch } from '../../hooks/redux';
import { formatOrderNumber } from '../../utils/orderNumber';
import { shouldUseCloudPrint } from '../../utils/cloudPrintRoute';

const { Title, Text } = Typography;
const { Option } = Select;

const OrdersList: React.FC = () => {
  const navigate = useNavigate();
  const { message: messageApi } = App.useApp();
  const { isMobile, isTablet } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Espejo del gate del backend (PaymentFlowRules.IsCashierRole) vía helper
  // compartido. Antes era `role !== 'mesero'`, que le ofrecía "Cobrar" a Chef y
  // se comía un 403.
  const canCobrar = canProcessPayments(user?.role);
  const canCancelar = canCancelOrders(user?.role);
  const canDescontar = canApplyDiscounts(user?.role);

  // El total de ingresos del filtro es dato sensible: solo gerentes y administradores
  const canSeeRevenue = isAdmin(user?.role) || isManager(user?.role);

  // Server-side filtering + pagination. `filters` is the single source of truth sent to the
  // backend; `searchInput` is kept separate so the text box stays responsive while the actual
  // searchTerm is debounced into `filters`. `dateRange` backs the DatePicker's value prop.
  const [filters, setFilters] = useState<OrderFilter>({ pageNumber: 1, pageSize: 20 });
  const [searchInput, setSearchInput] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>(undefined);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [selectedFacturaId, setSelectedFacturaId] = useState<number | null>(null);
  const [facturaContextOrderId, setFacturaContextOrderId] = useState<string | undefined>(undefined);
  const [showFacturaDetalleModal, setShowFacturaDetalleModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);

  // API Queries. Polling lento de fallback: el tiempo real de abajo es solo
  // optimización y la lista debe actualizarse aunque el websocket se caiga.
  const { data: ordersData, isLoading, refetch } = useGetOrdersPagedQuery(filters, {
    pollingInterval: 60000,
  });

  // Cualquier mutación de órdenes en el backend (crear, items, pagos, cierres,
  // transferencias…) emite OrdersChanged; refetcheamos con los filtros vigentes.
  // RECONNECTED se pone al día de lo perdido mientras el socket estuvo caído.
  useHubEvent(ORDERS_CHANGED_EVENT, refetch);
  useHubEvent(RECONNECTED_EVENT, refetch);

  const orders = ordersData?.items ?? [];
  const totalCount = ordersData?.totalCount ?? 0;
  const { data: paymentMethods = [] } = useGetPaymentMethodsQuery({ activeOnly: true });
  const [processPayment] = useProcessPaymentMutation();
  const [closeInternalOrder] = useCloseInternalConsumptionOrderMutation();
  const [cancelOrder, { isLoading: cancellingOrder }] = useCancelOrderMutation();
  const [applyDiscount] = useApplyDiscountMutation();
  const [orderToCancel, setOrderToCancel] = useState<Order | undefined>(undefined);
  // Per-order in-flight id: a hook-level isLoading would spin EVERY row's
  // "Cerrar" button while one order closes.
  const [closingInternalId, setClosingInternalId] = useState<string | null>(null);
  const { data: facturacionConfig } = useGetFacturacionConfigQuery();
  const { data: facturacionSetting } = useGetBoolSettingQuery('FacturacionActiva');
  const isFacturacionModuleEnabled = facturacionSetting?.value ?? false;
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  const taxRate = (restaurantSettings?.taxRate || 16) / 100; // Convert percentage to decimal

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();

  // Whether any user-facing filter is active (drives the empty-state message).
  const hasActiveFilters = !!(
    filters.searchTerm ||
    (filters.status && filters.status !== 'All') ||
    (filters.paymentMethodId && filters.paymentMethodId !== 'All') ||
    dateRange
  );

  // Search is debounced (500ms) into `filters` so we don't fire a request per keystroke.
  // Clearing the box (X button or deleting to empty) applies immediately. Every filter
  // change resets to page 1. Pattern mirrors FacturasManager.
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value) {
      setFilters((prev) => ({ ...prev, searchTerm: undefined, pageNumber: 1 }));
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 500);
  };

  const handleStatusChange = (status: OrderStatus | 'All') => {
    setFilters((prev) => ({ ...prev, status, pageNumber: 1 }));
  };

  const handlePaymentMethodChange = (paymentMethodId: string) => {
    setFilters((prev) => ({ ...prev, paymentMethodId, pageNumber: 1 }));
  };

  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    setDateRange(dates);
    setFilters((prev) => ({
      ...prev,
      fechaInicio: dates ? dates[0].format('YYYY-MM-DD') : undefined,
      fechaFin: dates ? dates[1].format('YYYY-MM-DD') : undefined,
      pageNumber: 1,
    }));
  };

  const handlePageChange = (page: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageNumber: page, pageSize: size }));
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleRequestInvoice = (order: Order) => {
    setSelectedOrder(order);
    setShowFacturaModal(true);
  };

  const handleViewFactura = (order: Order) => {
    if (order.facturaId) {
      // En una global la factura ampara muchas órdenes: el detalle necesita saber cuál es esta.
      setFacturaContextOrderId(order.id);
      setSelectedFacturaId(order.facturaId);
      setShowFacturaDetalleModal(true);
    }
  };

  const handlePayOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const handleSplitOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowSplitModal(true);
  };

  const handleDiscountOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowDiscountModal(true);
  };

  const handlePreCheckOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowPreCheckModal(true);
  };

  // El error NO se reporta aquí: DiscountModal ya lo pinta con el mensaje del backend
  // (que es el que explica los rechazos útiles, como "la orden tiene la cuenta dividida").
  // Solo se relanza para que el modal no se cierre ni felicite tras una mutación fallida.
  const handleApplyDiscount = async (discountData: DiscountData) => {
    if (!selectedOrder) return;
    await applyDiscount({ orderId: selectedOrder.id, ...discountData }).unwrap();
    // La mutación invalida tags por id de orden y esta lista provee el tag 'Order' pelón,
    // así que no la alcanza: sin este refetch el total y la elegibilidad para dividir se
    // quedan con el dato viejo hasta el polling de 60s.
    refetch();
  };

  // Post-cobro: la mesa la libera el backend (mesa a Sucia + TableClosed), aquí
  // solo se refresca lo que la pantalla tiene en caché. Se invalida 'Table' para
  // que el mapa de mesas no quede mostrando la mesa ocupada al volver a él.
  const refreshAfterPayment = () => {
    refetch();
    dispatch(tablesApi.util.invalidateTags(['Table']));
  };

  const handleReprint = async (order: Order) => {
    // Calculate totals for closed order (prices already include IVA)
    const activeItems = order.orderItems.filter(item => !item.isCancelled);
    const rawTotal = activeItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
    const discountAmount = order.totalDiscountAmount || 0;
    const total = rawTotal - discountAmount;
    const subtotal = total / (1 + taxRate);
    const tax = total - subtotal;

    // Los pagos y la propina reales, no un efectivo inventado.
    //
    // Antes esto era `[{ methodId: 'cash', amount: total }]` con `tip: 0`, así que la copia
    // de una cuenta cobrada $600 tarjeta + $400 efectivo con $150 de propina salía como
    // $1,000 en efectivo y sin propina. Si esa copia va a una aclaración con el banco o a un
    // reparto de propinas, no cuadra con nada. El ticket original (PaymentModal) sí imprime
    // los datos correctos: están en order.payments, que el DTO ya trae.
    const payments = (order.payments || []).map(p => ({
      methodId: p.paymentMethodCode || p.paymentMethodName,
      amount: p.amount,
      reference: p.reference || '',
    }));

    // Una orden cobrada siempre tiene pagos; el fallback solo cubre datos viejos o
    // incompletos, y se marca como desconocido en vez de afirmar "efectivo".
    if (payments.length === 0) {
      payments.push({ methodId: 'Sin registro', amount: total, reference: '' });
    }

    // La propina sigue en 0 y NO por decisión: no se persiste en la orden. Vive como
    // CashMovement de tipo TIP y en OrderSplit.TipAmount, ninguno de los dos expuesto en
    // OrderDto. Ponerle un número inventado sería peor; exponerla necesita tocar el DTO.

    // Build autofactura URL for reprint. Sin QR en una orden tomada sin conexión: hasta
    // sincronizar su número es 0 y no se puede facturar (ver PaymentModal).
    const autofacturaUrl = facturacionConfig?.autofacturaEnabled && facturacionConfig?.autofacturaCode
      && !order.offlineFolioLabel
      ? `${window.location.origin}/autofactura/${facturacionConfig.autofacturaCode}?order=${order.orderNumber}&total=${total.toFixed(2)}`
      : undefined;

    const preview = buildOrderTicketPreview({
      kind: 'cuenta',
      title: 'TICKET DE PAGO (COPIA)',
      tableLabel: order.tableName || 'Para llevar',
      waiterName: order.assignedWaiterName || undefined,
      orderLabel: formatOrderNumber(order),
      items: activeItems,
      subtotal,
      tax,
      discounts: (order.discounts || []).map(d => ({ reason: discountLabel(d, order.orderItems), discountAmount: d.discountAmount })),
      total,
      // Los mismos pagos que el papel (incluido el "Sin registro" de datos viejos), con el
      // nombre del método en vez del código cuando lo hay.
      payments: payments.map((p, i) => ({ method: order.payments?.[i]?.paymentMethodName || p.methodId, amount: p.amount })),
    });

    try {
      // Try Cloud Print first (if enabled)
      if (shouldUseCloudPrint(cloudPrintSettingOn, order)) {
        await withPrintAnimation(preview, () => cloudPrintJob({
          printType: 'Payment',
          // Field names must match the backend EscPosReceiptData DTO exactly
          // (JSON binding is case-insensitive but does not translate names).
          // DateTime is intentionally omitted: the backend stamps it in the
          // restaurant's timezone. See CloudPrintJobService.EnqueueAsync.
          receiptData: {
            orderNumber: order.orderNumber,
            restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
            address: restaurantSettings?.address,
            phone: restaurantSettings?.phone,
            title: 'TICKET DE PAGO',
            tableName: order.tableName || '',
            waiterName: order.assignedWaiterName || '',
            items: activeItems.map(item => ({
              name: item.productName,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              total: item.total,
              variants: item.variants?.map(v => v.variantName) || [],
            })),
            subtotal,
            tax,
            taxRate,
            discounts: (order.discounts || []).map(d => ({ reason: discountLabel(d, order.orderItems), amount: d.discountAmount })),
            tip: 0,
            total,
            payments: payments.map(p => ({ method: p.methodId, amount: p.amount, reference: p.reference || null })),
            autofacturaUrl,
          },
        }));
        messageApi.success('Ticket reimpreso exitosamente');
      } else {
      // Check print service availability
      const isPrintAgentAvailable = await printAgentService.isAvailable();
      const isBtAvailable = bluetoothPrintService.isAvailable();

      // Try Print Agent first (Windows)
      if (isPrintAgentAvailable) {
        const result = await withPrintAnimation(preview, () => printAgentService.printPayment(
          order,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          order.tableName || '',
          order.assignedWaiterName || '',
          subtotal,
          tax,
          taxRate,
          order.discounts || [],
          0, // tip
          total,
          payments,
          autofacturaUrl
        ));
        if (result.success) {
          messageApi.success('Ticket reimpreso exitosamente');
        } else {
          messageApi.error(`Error al reimprimir: ${result.message}`);
        }
      }
      // Try Bluetooth (Android)
      else if (isBtAvailable) {
        const result = await withPrintAnimation(preview, () => bluetoothPrintService.printPayment(
          order,
          restaurantSettings?.name || 'MASSRESTAURANT',
          restaurantSettings?.address,
          restaurantSettings?.phone,
          order.tableName || '',
          order.assignedWaiterName || '',
          subtotal,
          tax,
          taxRate,
          order.discounts || [],
          0,
          total,
          payments,
          autofacturaUrl
        ));
        if (result.success) {
          messageApi.success('Ticket reimpreso exitosamente');
        } else {
          messageApi.error(`Error al reimprimir: ${result.message}`);
        }
      } else {
        messageApi.warning('No hay servicio de impresion disponible');
      }
      }
    } catch (error: any) {
      messageApi.error('Error al reimprimir el ticket');
      console.error('Reprint error:', error);
    }
  };

  const handleProcessPayment = async (payments: PaymentEntry[], tip: number) => {
    if (!selectedOrder) return;

    try {
      // El id viaja como Id del pago: con él un reintento no cobra dos veces.
      const transformedPayments = payments.map(payment => ({
        id: payment.id,
        methodId: payment.methodId,
        amount: payment.amount,
        reference: payment.reference
      }));

      await processPayment({
        orderId: selectedOrder.id,
        // Obligatorio en órdenes de mesa: el backend valida que el tableId del
        // cobro sea el de la orden. TakeOut/Domicilio van sin mesa.
        tableId: selectedOrder.type === 'DineIn' ? selectedOrder.tableId : undefined,
        payments: transformedPayments,
        tip,
        discountAmount: 0,
        discountPercentage: 0,
      }).unwrap();

      messageApi.success('Pago procesado exitosamente');
      setShowPaymentModal(false);
      setSelectedOrder(undefined);
      refreshAfterPayment();
    } catch (error: any) {
      console.error('Payment error:', error);

      // La división se creó con esta lista ya stale (o desde otra pantalla), así
      // que se ofreció el cobro completo y el backend lo rechaza. Sin esto la
      // cajera se queda sin salida: se la manda al cobro por cuenta.
      if (isOrderAlreadySplitError(error)) {
        messageApi.warning(ORDER_ALREADY_SPLIT_NOTICE);
        goToSplitPayment();
        throw error;
      }

      // getErrorMessage y no `data?.message`: un 401 por token vencido llega sin
      // cuerpo, y con el acceso directo la cajera solo veía "Error al procesar el
      // pago" sin pista de que lo que hay que hacer es volver a iniciar sesión.
      messageApi.error(getRetryableWriteErrorMessage(error, 'Error al procesar el pago'));
      // Relanzar para que PaymentModal no imprima el ticket de un pago fallido
      throw error;
    }
  };

  // Salida compartida por el cobro manual y el de terminal: los dos chocan con
  // el mismo rechazo del backend cuando la orden ya está dividida.
  const goToSplitPayment = () => {
    setShowPaymentModal(false);
    setShowSplitModal(true); // selectedOrder se conserva a propósito
    refetch();
  };

  // Una orden sigue viva mientras no esté cerrada ni cancelada. Es el mismo
  // criterio que usa el backend para aceptar un cobro, así que no se listan
  // estados uno por uno: Preparando/Lista/Entregada también se cobran (una orden
  // para llevar ya entregada sigue debiendo dinero).
  const isLiveOrder = (order: Order) => {
    const status = order.status.toLowerCase();
    return status !== 'closed' && status !== 'cancelled';
  };

  const hasChargeableItems = (order: Order) =>
    (order.orderItems || []).some(item => !item.isCancelled);

  // Cobrar desde aquí funciona para CUALQUIER orden viva, mesas incluidas (antes
  // solo para llevar/domicilio): el flujo de cobro es idéntico y el backend libera
  // la mesa. Las órdenes divididas se cobran por cuenta, no completas.
  const canPayOrder = (order: Order) =>
    isLiveOrder(order)
    && canCobrar
    && hasChargeableItems(order)
    && !order.isInternalConsumption   // tiene su propio cierre sin cobro
    && !order.isSplit;                // el backend rechaza el cobro completo

  // Orden con la cuenta ya dividida: se cobra cuenta por cuenta.
  const canPaySplits = (order: Order) =>
    isLiveOrder(order) && canCobrar && !!order.isSplit;

  // Dividir la cuenta. Espeja OrderSplitService.GetSplitEligibilityError: solo
  // órdenes abiertas/listas, sin dividir, sin consumo interno y sin descuentos
  // activos (el prorrateo no compone con descuentos de orden).
  const canSplitOrder = (order: Order) => {
    const status = order.status.toLowerCase();
    return (status === 'open' || status === 'ready')
      && canCobrar
      && hasChargeableItems(order)
      && !order.isSplit
      && !order.isInternalConsumption
      && (order.totalDiscountAmount ?? 0) === 0;
  };

  // Aplicar descuentos. Espeja OrderService.ApplyDiscountAsync: orden viva y sin
  // dividir (ahí el descuento va por cuenta al cobrar). El consumo interno queda fuera
  // porque no se cobra: descontarle un porcentaje a algo que cierra en $0 no significa
  // nada. Hasta ahora esto solo existía en el menú de mesa, así que las órdenes para
  // llevar y a domicilio —que viven en mesas virtuales, fuera del mapa de mesas— no
  // tenían NINGÚN camino a un descuento.
  const canDiscountOrder = (order: Order) =>
    isLiveOrder(order)
    && canDescontar
    && hasChargeableItems(order)
    && !order.isSplit
    && !order.isInternalConsumption;

  // Precuenta desde aquí: solo para llevar y domicilio. Las de mesa la tienen en el
  // menú de la mesa, donde además cambia la mesa a estado Precuenta —housekeeping que
  // aquí no aplica porque no hay mesa real que marcar.
  //
  // Las divididas sí entran: la precuenta imprime un ticket por cuenta con lo
  // que le toca a cada quien. Antes quedaban fuera porque imprimía UN total que
  // no era lo que iba a pagar nadie.
  const canPreCheckOrder = (order: Order) =>
    isLiveOrder(order)
    && (order.type === 'TakeOut' || order.type === 'Delivery')
    && hasChargeableItems(order)
    && !order.isInternalConsumption;

  // Cancelar la orden completa: la salida para las levantadas por error. Al no
  // poder cobrarse (quedan en $0 si se cancelan sus renglones) tampoco se podían
  // cerrar, y se quedaban abiertas para siempre. Gate espejo del backend.
  const canCancelOrder = (order: Order) =>
    isLiveOrder(order) && canCancelar;

  const handleCancelOrder = async (reason: string) => {
    if (!orderToCancel) return;
    try {
      await cancelOrder({ id: orderToCancel.id, reason }).unwrap();
      messageApi.success(`Orden #${orderToCancel.orderNumber} cancelada`);
      setOrderToCancel(undefined);
      refetch();
    } catch (error: unknown) {
      messageApi.error(getErrorMessage(error, 'No se pudo cancelar la orden'));
    }
  };

  // Consumo interno: close without payment (deducts inventory). Same role gate
  // as cobrar so meseros can't write off stock on their own.
  const canCloseInternal = (order: Order) =>
    !!order.isInternalConsumption && order.status.toLowerCase() === 'open' && canCobrar;

  const handleCloseInternal = async (order: Order) => {
    setClosingInternalId(order.id);
    try {
      await closeInternalOrder(order.id).unwrap();
      messageApi.success('Consumo interno cerrado; inventario descontado');
      refetch();
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al cerrar el consumo interno');
    } finally {
      setClosingInternalId(null);
    }
  };

  // One rendering for the confirm+close action, shared by desktop row and
  // mobile card so copy/behavior can't drift between layouts.
  const renderCloseInternalButton = (order: Order, style?: React.CSSProperties) => (
    <Popconfirm
      title="Cerrar consumo interno"
      description="Se descontará el inventario de esta orden. ¿Continuar?"
      okText="Cerrar"
      cancelText="Cancelar"
      onConfirm={() => handleCloseInternal(order)}
    >
      <Button
        type="primary"
        size="small"
        icon={<CheckOutlined />}
        loading={closingInternalId === order.id}
        onClick={(e) => e.stopPropagation()}
        style={style}
      >
        Cerrar
      </Button>
    </Popconfirm>
  );

  // Check if order can be edited (TakeOut/Delivery with Open status)
  const canEditOrder = (order: Order) => {
    const isTakeOutOrDelivery = order.type === 'TakeOut' || order.type === 'Delivery';
    const isOpen = order.status.toLowerCase() === 'open';
    return isTakeOutOrDelivery && isOpen;
  };

  const handleEditOrder = (order: Order) => {
    const editPath = order.isInternalConsumption
      ? `/new-order/internal?edit=${order.id}`
      : order.type === 'TakeOut'
        ? `/new-order/takeout?edit=${order.id}`
        : `/new-order/delivery?edit=${order.id}`;
    navigate(editPath);
  };

  const getStatusTag = (status: OrderStatus) => {
    // Normalize status to PascalCase (backend sends lowercase)
    const normalizedStatus = (status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()) as OrderStatus;

    // Honest colors: Closed is a SUCCESSFUL outcome (paid), so it's neutral —
    // red is reserved for Cancelled. Green marks the live/actionable ones.
    const statusConfig: Record<OrderStatus, { color: string; text: string }> = {
      Open: { color: 'green', text: 'Abierta' },
      Preparing: { color: 'orange', text: 'Preparando' },
      Ready: { color: 'gold', text: 'Lista' },
      Delivered: { color: 'blue', text: 'Entregada' },
      Closed: { color: 'default', text: 'Cerrada' },
      Cancelled: { color: 'red', text: 'Cancelada' },
    };

    const config = statusConfig[normalizedStatus];
    if (!config) {
      return <Tag>{status}</Tag>;
    }
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Order type is metadata, not a traffic light: small icon + muted text,
  // shown under the order number ("🍽 Mesa 4") instead of a colored tag.
  const getTypeMeta = (order: Order): string => {
    if (order.isInternalConsumption) return '🍴 Consumo interno';
    switch (order.type) {
      case 'DineIn': return `🍽 ${order.tableName ? `Mesa ${order.tableName}` : 'Mesa'}`;
      case 'TakeOut': return '🥡 Para llevar';
      case 'Delivery': return '🛵 Domicilio';
      default: return order.type;
    }
  };

  // "38 min" / "1h 45m" — open → close, or open → now for live orders
  const formatDuration = (openTime: string, closeTime?: string): string => {
    const end = closeTime ? dayjs(closeTime) : dayjs();
    const minutes = Math.max(0, end.diff(dayjs(openTime), 'minute'));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  };

  const renderHorario = (order: Order) => (
    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
      <div>{formatInTimeZone(order.openTime, 'DD/MM HH:mm')}</div>
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        {order.closeTime
          ? `→ ${formatInTimeZone(order.closeTime, 'HH:mm')} · ${formatDuration(order.openTime, order.closeTime)}`
          : order.status.toLowerCase() === 'open'
            ? `en curso · ${formatDuration(order.openTime)}`
            : '—'}
      </Text>
    </div>
  );

  // El total de la orden llega BRUTO: order.total es la suma de renglones activos y los
  // descuentos viajan aparte en totalDiscountAmount (ver MappingProfile). Pintarlo tal cual
  // tenía dos consecuencias: aplicar un descuento desde esta pantalla no movía la cifra —se
  // leía como que no había funcionado— y una cuenta ya cerrada con descuento se listaba por
  // encima de lo que de verdad se cobró.
  const renderTotal = (order: Order, opts?: { fontSize?: number; color?: string }) => {
    const discount = order.totalDiscountAmount ?? 0;
    const net = Math.max(0, order.total - discount);
    return (
      <div style={{ fontVariantNumeric: 'tabular-nums' }}>
        <Text strong style={{ fontSize: opts?.fontSize, color: opts?.color }}>
          ${net.toFixed(2)}
        </Text>
        {discount > 0 && (
          <div>
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              <span style={{ textDecoration: 'line-through' }}>${order.total.toFixed(2)}</span>
              {' '}· desc. ${discount.toFixed(2)}
            </Text>
          </div>
        )}
      </div>
    );
  };

  // Condensed factura cell: one line + folio·origen, instead of 2 tags + folio
  const renderFactura = (order: Order) => {
    if (!order.facturaId) return <Text type="secondary">—</Text>;
    // El backend escribe "Timbrada"/"Cancelada"; aquí se comparaba contra "Timbrado" y
    // "Cancelado", así que ninguna salía en verde ni en rojo.
    const visible = estadoVisibleFactura(order.facturaEstado);
    const color = visible === 'Timbrada'
      ? 'var(--color-success-text)'
      : visible === 'Cancelada'
        ? 'var(--color-error-text)'
        : 'var(--color-warning-text)';
    const check = visible === 'Timbrada' ? '✓ ' : '';
    return (
      <div style={{ fontSize: 12 }}>
        <Text strong style={{ color, fontSize: 12 }}>{check}{ETIQUETA_ESTADO_FACTURA[visible].texto}</Text>
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {order.facturaFolio}
            {order.facturaSolicitadoPor === 'Portal' ? ' · Portal' : ''}
          </Text>
        </div>
      </div>
    );
  };

  // One visible contextual action per row; everything else lives in the ⋯ menu.
  const getRowMenuItems = (order: Order): MenuProps['items'] => [
    {
      key: 'view',
      label: 'Ver detalles',
      icon: <EyeOutlined />,
      onClick: () => handleViewDetails(order),
    },
    ...(canEditOrder(order) ? [{
      key: 'edit',
      label: 'Editar',
      icon: <EditOutlined />,
      onClick: () => handleEditOrder(order),
    }] : []),
    ...(canPreCheckOrder(order) ? [{
      key: 'precuenta',
      label: 'Precuenta',
      icon: <PrinterOutlined />,
      onClick: () => handlePreCheckOrder(order),
    }] : []),
    ...(canDiscountOrder(order) ? [{
      key: 'descuentos',
      label: 'Aplicar descuentos',
      icon: <PercentageOutlined />,
      onClick: () => handleDiscountOrder(order),
    }] : []),
    ...(canSplitOrder(order) ? [{
      key: 'split',
      label: 'Dividir cuenta',
      icon: <ScissorOutlined />,
      onClick: () => handleSplitOrder(order),
    }] : []),
    ...(order.status.toLowerCase() === 'closed' ? [{
      key: 'reprint',
      label: 'Reimprimir ticket',
      icon: <PrinterOutlined />,
      onClick: () => handleReprint(order),
    }] : []),
    ...(isFacturacionModuleEnabled && order.facturaId ? [{
      key: 'factura',
      label: 'Ver factura',
      icon: <FileTextOutlined />,
      onClick: () => handleViewFactura(order),
    }] : []),
    // Destructivo: va al final, separado y en rojo.
    ...(canCancelOrder(order) ? [{
      type: 'divider' as const,
      key: 'cancel-divider',
    }, {
      key: 'cancel-order',
      danger: true,
      label: 'Cancelar orden',
      icon: <StopOutlined />,
      onClick: () => setOrderToCancel(order),
    }] : []),
  ];

  // Cobro de una orden ya dividida: abre la división en vez del cobro completo.
  const renderPaySplitsButton = (order: Order, style?: React.CSSProperties) => (
    <Button
      type="primary"
      size="small"
      icon={<ScissorOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        handleSplitOrder(order);
      }}
      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', ...style }}
    >
      Cobrar cuentas
    </Button>
  );

  const renderPayButton = (order: Order, style?: React.CSSProperties) => (
    <Button
      type="primary"
      size="small"
      icon={<DollarOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        handlePayOrder(order);
      }}
      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', ...style }}
    >
      Cobrar
    </Button>
  );

  const renderContextualAction = (order: Order) => {
    if (canCloseInternal(order)) {
      return renderCloseInternalButton(order);
    }
    if (canPaySplits(order)) {
      return renderPaySplitsButton(order);
    }
    if (canPayOrder(order)) {
      return renderPayButton(order);
    }
    // Con su factura cancelada ante el SAT, la orden vuelve a poder facturarse.
    if (isFacturacionModuleEnabled && facturacionConfig?.facturacionEnabled
        && order.status === 'Closed' && (!order.facturaId || order.facturaEstado === 'Cancelada')
        && !order.isInternalConsumption) {
      return (
        <Button
          type="primary"
          size="small"
          icon={<FileTextOutlined />}
          onClick={() => handleRequestInvoice(order)}
        >
          Facturar
        </Button>
      );
    }
    return null;
  };

  // "Orden" cell: number as the identity, type + table as metadata below.
  const renderOrderCell = (record: Order) => (
    <div>
      <Text strong style={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
        {formatOrderNumber(record)}
      </Text>
      <div>
        <Text type="secondary" style={{ fontSize: 11.5 }}>{getTypeMeta(record)}</Text>
      </div>
    </div>
  );

  // No column sorters: with server-side pagination they only reordered the
  // current page (20 of N rows), which is misleading. Natural order is
  // newest-first from the backend.

  // Columns for tablet - simplified
  const tabletColumns: ColumnsType<Order> = [
    {
      title: 'Orden',
      key: 'order',
      width: 130,
      render: (_, record) => renderOrderCell(record),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: OrderStatus) => getStatusTag(status),
    },
    {
      title: 'Horario',
      key: 'horario',
      width: 130,
      render: (_, record) => renderHorario(record),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 90,
      align: 'right',
      render: (_, record) => renderTotal(record),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right',
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
          {renderContextualAction(record)}
          <Dropdown menu={{ items: getRowMenuItems(record) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  // Full columns for desktop — 7 columns, no horizontal scroll
  const desktopColumns: ColumnsType<Order> = [
    {
      title: 'Orden',
      key: 'order',
      width: 150,
      render: (_, record) => renderOrderCell(record),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: OrderStatus) => getStatusTag(status),
    },
    {
      title: 'Horario',
      key: 'horario',
      width: 170,
      render: (_, record) => renderHorario(record),
    },
    {
      title: 'Atención',
      key: 'atencion',
      width: 150,
      render: (_, record) => (
        <div>
          <Text>{record.assignedWaiterName || '—'}</Text>
          {(record.adults !== undefined || record.children !== undefined) && (
            <div>
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                {record.adults || 0}A{(record.children || 0) > 0 ? ` / ${record.children}N` : ''}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 110,
      align: 'right',
      render: (_, record) => renderTotal(record, { fontSize: 14 }),
    },
    {
      title: 'Factura',
      key: 'factura',
      width: 140,
      render: (_, record) => renderFactura(record),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      align: 'right',
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
          {renderContextualAction(record)}
          <Dropdown menu={{ items: getRowMenuItems(record) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  // Select columns based on screen size
  const columns = isTablet ? tabletColumns : desktopColumns;

  // Mobile Order Card Component
  const OrderCard: React.FC<{ order: Order }> = ({ order }) => (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      hoverable
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: 1, minWidth: 0 }}
          onClick={() => handleViewDetails(order)}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text strong style={{ fontSize: 16 }}>{formatOrderNumber(order)}</Text>
              {getStatusTag(order.status)}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)' }}>
              <span>{getTypeMeta(order)}</span>
              <span>{formatInTimeZone(order.openTime, 'DD/MM HH:mm')}</span>
              {order.assignedWaiterName && <span>{order.assignedWaiterName}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {renderTotal(order, { fontSize: 16, color: '#52c41a' })}
            {order.facturaId && (
              <div>
                <Tag color={ETIQUETA_ESTADO_FACTURA[estadoVisibleFactura(order.facturaEstado)].color} style={{ fontSize: 10, margin: 0 }}>
                  {ETIQUETA_ESTADO_FACTURA[estadoVisibleFactura(order.facturaEstado)].texto}
                </Tag>
                {order.facturaSolicitadoPor === 'Portal' && (
                  <Tag color="blue" style={{ fontSize: 10, margin: 0, marginLeft: 2 }}>Portal</Tag>
                )}
              </div>
            )}
          </div>
        </div>
        {/* El mismo menú ⋯ de escritorio. En móvil no existía, así que todo lo que no
            fuera un botón de la fila de abajo (precuenta, descuentos, cancelar, ver
            detalles) era sencillamente inalcanzable desde un celular. */}
        <Dropdown menu={{ items: getRowMenuItems(order) }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label="Más acciones"
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </div>
      {/* Acciones de la orden: cobrar (mesa o para llevar), dividir, editar o
          cerrar consumo interno. Dividir se repite aquí como botón por frecuencia. */}
      {(canEditOrder(order) || canPayOrder(order) || canPaySplits(order)
        || canSplitOrder(order) || canCloseInternal(order)) && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-color)', display: 'flex', gap: 8 }}>
          {canEditOrder(order) && (
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditOrder(order);
              }}
              style={{ flex: 1 }}
            >
              Editar
            </Button>
          )}
          {canSplitOrder(order) && (
            <Button
              type="default"
              size="small"
              icon={<ScissorOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleSplitOrder(order);
              }}
              style={{ flex: 1 }}
            >
              Dividir
            </Button>
          )}
          {canPaySplits(order) && renderPaySplitsButton(order, { flex: 1 })}
          {canPayOrder(order) && renderPayButton(order, { flex: 1 })}
          {canCloseInternal(order) && renderCloseInternalButton(order, { flex: 1 })}
        </div>
      )}
    </Card>
  );

  if (isLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Cargando ordenes...</Text>
          </div>
        </div>
      </Card>
    );
  }

  const statusPillOptions = [
    { label: 'Todas', value: 'All' },
    { label: 'Abiertas', value: 'Open' },
    { label: 'Cerradas', value: 'Closed' },
    { label: 'Canceladas', value: 'Cancelled' },
  ];

  const pulseChipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    height: 26,
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color-strong)',
    borderRadius: 999,
    padding: '0 12px',
    fontSize: 12.5,
    color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: isMobile ? 0 : undefined }}>
      {/* Single bar: title + filter pulse + search + new order. The old subtitle and
          the manual refresh button are gone (every filter change refetches). */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 12,
      }}>
        <Title level={isMobile ? 4 : 3} style={{ margin: 0, marginRight: 4 }}>Órdenes</Title>
        <span style={pulseChipStyle}>
          <Text strong style={{ fontSize: 13 }}>{totalCount}</Text>&nbsp;órdenes
        </span>
        {canSeeRevenue && ordersData?.totalAmount != null && (
          <span style={pulseChipStyle}>
            del filtro&nbsp;
            <Text strong style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              ${ordersData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Text>
          </span>
        )}
        <div style={{ flex: 1 }} />
        {!isMobile && (
          <Input
            placeholder="Buscar # orden, mesa, mesero…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: 260 }}
          />
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/new-order')}
        >
          {isMobile ? 'Nueva' : 'Nueva Orden'}
        </Button>
      </div>

      {/* Filters: status as one-click pills, date + payment method as secondary */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginBottom: 12,
      }}>
        {isMobile && (
          <Input
            placeholder="Buscar orden…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            style={{ width: '100%' }}
          />
        )}
        <Segmented
          size={isMobile ? 'small' : 'middle'}
          value={filters.status ?? 'All'}
          onChange={(value) => handleStatusChange(value as OrderStatus | 'All')}
          options={statusPillOptions}
        />
        <MobileDateRangePicker
          style={{ width: isMobile ? '100%' : 240 }}
          placeholder={['Fecha inicio', 'Fecha fin']}
          value={dateRange}
          onChange={(dates) => handleDateRangeChange(dates as [Dayjs, Dayjs] | null)}
        />
        <Select
          style={{ width: isMobile ? '100%' : 190 }}
          placeholder="Método de pago"
          value={filters.paymentMethodId ?? 'All'}
          onChange={handlePaymentMethodChange}
        >
          <Option value="All">Todos los métodos</Option>
          {paymentMethods.map((pm) => (
            <Option key={pm.id} value={pm.id}>{pm.name}</Option>
          ))}
        </Select>
      </div>

      <Card styles={{ body: { padding: isMobile ? 12 : 16 } }}>

        {/* Orders - Cards for mobile, Table for tablet/desktop */}
        {orders.length === 0 ? (
          <Empty
            description={hasActiveFilters ? 'No se encontraron ordenes' : 'No hay ordenes'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : isMobile ? (
          // Mobile: Card list (server already returns just the current page)
          <div>
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
            {/* Simple pagination for mobile, driven by the server envelope */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <Button
                disabled={!ordersData?.hasPreviousPage}
                onClick={() => handlePageChange((ordersData?.pageNumber ?? 1) - 1, filters.pageSize ?? 20)}
              >
                Anterior
              </Button>
              <span style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>
                {ordersData?.pageNumber ?? 1} / {ordersData?.totalPages ?? 1}
              </span>
              <Button
                disabled={!ordersData?.hasNextPage}
                onClick={() => handlePageChange((ordersData?.pageNumber ?? 1) + 1, filters.pageSize ?? 20)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        ) : (
          // Tablet/Desktop: Table with server-side pagination. The whole row opens
          // the details modal (actions cell stops propagation).
          <Table<Order>
            columns={columns}
            dataSource={orders}
            rowKey="id"
            size={isTablet ? 'small' : 'middle'}
            onRow={(record) => ({
              onClick: () => handleViewDetails(record),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: ordersData?.pageNumber ?? 1,
              pageSize: ordersData?.pageSize ?? 20,
              total: totalCount,
              showSizeChanger: !isTablet,
              showTotal: isTablet ? undefined : (total, range) => `${range[0]}-${range[1]} de ${total} ordenes`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: handlePageChange,
            }}
            scroll={{ x: isTablet ? 540 : 960 }}
            childrenColumnName="__children__"
          />
        )}
      </Card>

      {/* Invoice Request Modal */}
      <SolicitarFacturaModal
        visible={showFacturaModal}
        onCancel={() => {
          setShowFacturaModal(false);
          setSelectedOrder(undefined);
        }}
        order={selectedOrder}
        onSuccess={(factura) => {
          messageApi.success(`Factura ${factura.folio} generada exitosamente`);
          setShowFacturaModal(false);
          setSelectedOrder(undefined);
        }}
      />

      {/* Order Details Modal */}
      <OrderDetailsModal
        visible={showDetailsModal}
        order={selectedOrder}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedOrder(undefined);
        }}
        onReprint={(order) => {
          handleReprint(order);
          setShowDetailsModal(false);
          setSelectedOrder(undefined);
        }}
        onRefresh={() => {
          refetch(); // Refrescar lista de órdenes cuando cambia el estado de facturación
        }}
      />

      <CancelOrderModal
        visible={!!orderToCancel}
        order={orderToCancel}
        loading={cancellingOrder}
        onCancel={() => setOrderToCancel(undefined)}
        onConfirm={handleCancelOrder}
      />

      {/* Factura Details Modal */}
      {selectedFacturaId && (
        <FacturaDetalleModal
          facturaId={selectedFacturaId}
          contextOrderId={facturaContextOrderId}
          visible={showFacturaDetalleModal}
          onClose={() => {
            setShowFacturaDetalleModal(false);
            setSelectedFacturaId(null);
            refetch(); // Refrescar lista de ordenes para ver el nuevo estado
          }}
          onFacturaRegenerated={(newFacturaId) => {
            // Cuando la factura se regenera, abrir la nueva factura
            setSelectedFacturaId(newFacturaId);
            setShowFacturaDetalleModal(true);
            refetch(); // Refrescar lista de ordenes
          }}
        />
      )}

      {/* Cobro de cualquier orden viva (mesa, para llevar o domicilio). No se pasa
          `table`: aquí no hay objeto Table y el modal deriva la etiqueta de la orden. */}
      <PaymentModal
        visible={showPaymentModal}
        onCancel={() => {
          setShowPaymentModal(false);
          setSelectedOrder(undefined);
        }}
        order={selectedOrder}
        onProcessPayment={handleProcessPayment}
        onOrderAlreadySplit={goToSplitPayment}
        terminalTarget={selectedOrder ? {
          orderId: selectedOrder.id,
          tableId: selectedOrder.type === 'DineIn' ? selectedOrder.tableId : undefined,
        } : undefined}
        onTerminalPaymentSettled={() => {
          // El webhook ya registró el pago, cerró la orden y liberó la mesa.
          setShowPaymentModal(false);
          setSelectedOrder(undefined);
          refreshAfterPayment();
          messageApi.success('Pago procesado exitosamente');
        }}
      />

      {/* Descuentos de orden. No se pasa `table` —no hay objeto Table aquí y las de
          para llevar/domicilio no tienen mesa real: el modal se identifica con la orden. */}
      <DiscountModal
        visible={showDiscountModal}
        onCancel={() => {
          setShowDiscountModal(false);
          setSelectedOrder(undefined);
          refetch(); // pudo quitarse un descuento sin aplicar ninguno nuevo
        }}
        order={selectedOrder}
        onApplyDiscount={handleApplyDiscount}
        onDiscountRemoved={refetch}
        onItemDiscountApplied={refetch}
      />

      {/* Precuenta de para llevar / domicilio. Sin `onPrintAndSave`: aquí la precuenta
          es solo el papel, no hay mesa que pasar a estado Precuenta. */}
      <PreCheckModal
        visible={showPreCheckModal}
        onCancel={() => {
          setShowPreCheckModal(false);
          setSelectedOrder(undefined);
        }}
        order={selectedOrder}
      />

      {/* División de cuenta: crear la división y cobrar cada cuenta, igual que
          desde el menú de la mesa */}
      <SplitBillModal
        visible={showSplitModal}
        onCancel={() => {
          setShowSplitModal(false);
          setSelectedOrder(undefined);
          refetch(); // isSplit pudo cambiar (división creada o cancelada)
        }}
        order={selectedOrder}
        onOrderClosed={() => {
          setShowSplitModal(false);
          setSelectedOrder(undefined);
          refreshAfterPayment();
        }}
      />
    </div>
  );
};

export default OrdersList;
